import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { List } from 'immutable';
import { isArray } from 'lodash';
import { Accordion } from 'react-bootstrap';
import { injectIntl } from 'react-intl';
import Button from '../ui/Button';
import Loader from '../ui/Loader';
import { allowDropHandler } from '../../utils/uiUtils';
import { parseMetatags, parseMetaValues } from '../../utils/metadataUtils';
import { SearchParams } from '../../utils/searchUtils';
import { metaCopy, METATAG_GROUP_EXCEPTIONS } from '../../actions/metaAttach';
import { stringifiedToDraggable } from '../../utils/draggables';
import {
  META_COPY_STATE_IN_PROGRESS,
  META_ATTACH_STATE_FETCHING_DATA,
  META_COPY_STATE_FAILURE,
  META_COPY_STATE_SUCCESS,
} from '../../constants/metaAttachStates';
import { deselectAll } from '../../actions/umoGallery';
import PictionJS from 'piction-js';
import UMOThumbnail from '../UMOThumbnail';
import { retrieveUMO } from '../../actions/umo';
import appConfig from '../../appConfig';

function MetaCopy({
  intl,
  dispatch,
  metaAttachState,
  metaAttachError,
  allMetatags,
  umo,
  selectedUMOIDs,
  searchParams,
  isSelectAll,
  umoTotal,
}) {
  const [metatags, setMetatags] = useState([]);
  const [isReplace, setReplace] = useState(true);
  const isLoading = metaAttachState === META_COPY_STATE_IN_PROGRESS;
  const isFetchingData = metaAttachState === META_ATTACH_STATE_FETCHING_DATA;
  const isMaxItemsReached = (isSelectAll && umoTotal > appConfig.advanced.metaCopyMaxItems)
  || (!isSelectAll && selectedUMOIDs.count() > appConfig.advanced.metaCopyMaxItems);
  const isMaxResultsError = metaAttachError?.a?.RETURN_MESSAGE?.[0]?.$.e.toLowerCase().includes('max results')
  || isMaxItemsReached;

  useEffect(() => {
    if (!isFetchingData) {
      dispatch(retrieveUMO(umo.id, false))
        .then(res => {
          const filteredTags = allMetatags.toJS()[res.data.a[0]]
            .filter(tag => {
              const rawTag = Array.isArray(tag) ? tag[0] : tag;
              return !METATAG_GROUP_EXCEPTIONS.includes(rawTag.CATEGORY[0]);
            });
          const valueTags = parseMetaValues(res.metadataList.all, true);
          const parsedTags = parseMetatags(filteredTags, valueTags, true);
          setMetatags(parsedTags);
        });
    }
  }, [umo.id]);

  const attachMetadata = (umoIDList, params) => {
    dispatch(metaCopy(umo.id, umoIDList, params, intl))
      .then(() => dispatch(deselectAll()));
  };

  const onAttachSelectedClick = () => {
    attachMetadata(selectedUMOIDs.toJS(),
      isSelectAll ? searchParams : null);
  };

  const dropHandler = (e) => {
    e.preventDefault();
    const draggable = stringifiedToDraggable(e.dataTransfer.getData('text'));
    if (!(draggable instanceof Error)) {
      attachMetadata(
        isArray(draggable.getSelectionList()) ?
          draggable.getSelectionList() : [],
        draggable.getIsSelectAll() ? draggable.getSearchParams() : null
      );
    }
  };

  const getMetatagVal = (value) => {
    if (isArray(value)) {
      if (isArray(value[0])) { // repeating group
        const dispValue = [];
        // eslint-disable-next-line no-use-before-define
        value.forEach(repMeta => dispValue.push(getDisplayTags(repMeta)));
        return <ul className="meta-attach__cur-inner-list">{dispValue}</ul>;
      }  // repeating field
      return (
        <ul className="meta-attach__cur-inner-list">{value.map(v =>
          <li className="meta-attach__cur-list-item" key={v}>{v}</li>)}
        </ul>
      );
    } return value;
  };

  const getDisplayTags = (tags) => {
    const metas = [];
    tags.forEach(meta => {
      metas.push(<li className="meta-attach__cur-list-item"
        key={`mt-edt-${meta.id}`}>{meta.name} - {getMetatagVal(meta.value)}</li>);
    });
    return metas;
  };


  const isDisabled = isLoading || (metatags && metatags.length === 0) || (isSelectAll && !searchParams)
    || (!isSelectAll && selectedUMOIDs.count() < 1) || isMaxItemsReached;

  return (
    <div className="meta-attach__umo-copy meta-attach__drop-area">
      <div
        className="meta-attach__controls-container"
        onDrop={dropHandler}
        onDragOver={isDisabled ? undefined : allowDropHandler}
      >
        <UMOThumbnail umo={umo} noSensitiveOverlay />
        <label className="col-sm-12">{umo.name}</label>

        { // don't display the drop area on IE browsers
          navigator.msSaveBlob ? null :
              <div className="meta-attach__drop-inner">
                <i className="meta-attach__drop-inner-icon" />
                <p className="meta-attach__drop-inner-text">
                  {intl.formatMessage({id: 'metaAttach.metaCopyDragDrop'},
                    { umoName: umo.name })}
                </p>
            </div>
        }
        <Button
          primary
          disabled={isDisabled}
          onClick={onAttachSelectedClick}
          className="meta-attach__submit-btn"
        >
          { isLoading ? <Loader /> : intl.formatMessage({ id: 'umoGridItem.menuMetaCopy' }) }
        </Button>
        { metaAttachState === META_COPY_STATE_FAILURE || isMaxResultsError ?
          <div className="invalid-feedback" style={{ display: 'block', 'marginBottom': '5px'}}>
            {isMaxResultsError
              ? intl.formatMessage({ id: 'metaAttach.maxResultsError' },
                { maxItems: appConfig.advanced.metaCopyMaxItems })
              : intl.formatMessage({ id: 'general.error' })}
          </div> : null
        }
        { metaAttachState === META_COPY_STATE_SUCCESS ?
          <div className="valid-feedback" style={{ display: 'block', 'marginBottom': '5px'}}>
            {intl.formatMessage({ id: 'categoryMoveDialog.successCopyMessage' })}
          </div> : null
        }
        <Accordion>
          <Accordion.Item eventKey="0">
            <Accordion.Header>
              <div>
                <i className="fas fa-chevron-down meta-attach__cur-values" />
                {intl.formatMessage({ id: 'metaAttach.currentValues' })}
              </div>
            </Accordion.Header>
              <Accordion.Body>
                <ul className="meta-attach__cur-list">
                  {getDisplayTags(metatags)}
                </ul>
              </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      </div>
    </div>
  );
}

MetaCopy.propTypes = {
  metaAttachState: PropTypes.string,
  metaAttachError: PropTypes.instanceOf(Object),
  allMetatags: PropTypes.instanceOf(Object),
  dispatch: PropTypes.func,
  umo: PropTypes.instanceOf(PictionJS.UMO),
  selectedUMOIDs: PropTypes.instanceOf(List),
  searchParams: PropTypes.instanceOf(SearchParams),
  isSelectAll: PropTypes.bool,
  umoTotal: PropTypes.number,
};

export default injectIntl(MetaCopy);
